import React from 'react'

// 首頁《C++ 從入門到專家》課程地圖。原本是無原始檔的 flow.png，2026-10-31 改成內嵌 SVG：
// 節點、箭頭、說明、圖例都是下面的資料，改課名或加課只要改 NODES／EDGES，箭頭會自動接到框邊。
// 座標以原圖 1308×743 量出來，SVG 照寬度等比縮放。
const W = 1308
const H = 743
const FONT = '"PingFang TC", "Noto Sans TC", "Microsoft JhengHei", sans-serif'
// 中文粗體用實體粗體字型，不靠 font-weight 合成
const FONT_BOLD = '"Feis Bold TC", sans-serif' // Home.css 用 @font-face local() 指名的實體粗體
const RED = "#db5b5b"
const GREEN = "#316b1b"
const BORDER = "#4f4f4f"
const INK = "#141414"

type Node = { id: string, lines: string[], x: number, y: number, w: number, h: number, main?: boolean }
// main：系列的起點，紅框紅字
const NODES: Node[] = [
    {id: "project", lines: ["C++ 專案", "開發基礎實戰"], x: 94, y: 210, w: 330, h: 112},
    {id: "advanced", lines: ["C++ 進階程式設計"], x: 467, y: 226, w: 332, h: 113, main: true},
    {id: "algorithm", lines: ["C++ 演算法與", "程式解題入門"], x: 855, y: 210, w: 330, h: 112},
    {id: "oop", lines: ["C++ 物件導向", "與設計模式基礎"], x: 94, y: 371, w: 330, h: 112},
    {id: "ds", lines: ["C++ 資料結構與", "泛型程式設計基礎"], x: 467, y: 387, w: 332, h: 112},
    {id: "thread", lines: ["C++ 多執行緒與", "並行程式設計基礎"], x: 855, y: 371, w: 330, h: 112},
]

// prereq：A 是 B 的先修（紅色粗箭頭）；helpful：修過 A 對修 B 有幫助（綠色細箭頭）；both：雙向。
// fromAt／toAt：箭頭在框內的出發點（0～1 的相對位置），不寫就從中心連；斜向的箭頭用它對準框角。
type Edge = { from: string, to: string, kind: "prereq" | "helpful", both?: boolean, fromAt?: number[], toAt?: number[] }
const EDGES: Edge[] = [
    {from: "advanced", to: "project", kind: "helpful"},
    {from: "advanced", to: "algorithm", kind: "helpful"},
    {from: "advanced", to: "oop", kind: "prereq", fromAt: [0.08, 0.8], toAt: [0.92, 0.15]},
    {from: "advanced", to: "ds", kind: "prereq"},
    {from: "advanced", to: "thread", kind: "prereq", fromAt: [0.92, 0.8], toAt: [0.08, 0.15]},
    {from: "project", to: "oop", kind: "helpful"},
    {from: "ds", to: "oop", kind: "helpful"},
    {from: "ds", to: "thread", kind: "helpful"},
    {from: "ds", to: "algorithm", kind: "helpful", both: true, fromAt: [0.92, 0.15], toAt: [0.08, 0.85]},
]

const DESCRIPTION = ["最完整的現代 C++ 線上中文課程", "十八年的教學經驗累積", "從無到有建構 C++ 程式設計能力"]

const RADIUS = 52
const GAP = 6 // 箭頭與框之間留的空隙

// 點是否在圓角矩形內（含外擴 pad）
function inside(n: Node, px: number, py: number, pad: number) {
    const r = Math.min(RADIUS, n.h / 2) + pad
    const x0 = n.x - pad, y0 = n.y - pad, x1 = n.x + n.w + pad, y1 = n.y + n.h + pad
    if (px < x0 || px > x1 || py < y0 || py > y1) return false
    const cx = Math.min(Math.max(px, x0 + r), x1 - r)
    const cy = Math.min(Math.max(py, y0 + r), y1 - r)
    return (px - cx) ** 2 + (py - cy) ** 2 <= r * r
}

// 從框內的 (cx, cy) 往 (tx, ty) 走，回傳剛好離開框（加空隙）的那一點
function exitPoint(n: Node, cx: number, cy: number, tx: number, ty: number, pad: number) {
    let lo = 0, hi = 1
    for (let i = 0; i < 30; i++) {
        const mid = (lo + hi) / 2
        if (inside(n, cx + (tx - cx) * mid, cy + (ty - cy) * mid, pad)) lo = mid
        else hi = mid
    }
    return [cx + (tx - cx) * hi, cy + (ty - cy) * hi]
}

function edgeLine(e: Edge) {
    const a = NODES.find((n) => n.id === e.from)!, b = NODES.find((n) => n.id === e.to)!
    const at = (n: Node, f = [0.5, 0.5]) => [n.x + n.w * f[0], n.y + n.h * f[1]]
    const ac = at(a, e.fromAt), bc = at(b, e.toAt)
    const [x1, y1] = exitPoint(a, ac[0], ac[1], bc[0], bc[1], GAP + (a.main ? 2 : 0))
    const [x2, y2] = exitPoint(b, bc[0], bc[1], ac[0], ac[1], GAP)
    return {x1, y1, x2, y2}
}

const arrowStyle = (kind: Edge["kind"], both?: boolean) => kind === "prereq"
    ? {stroke: RED, strokeWidth: 5, markerEnd: "url(#course-map-red)"}
    : {stroke: GREEN, strokeWidth: 1.6, markerEnd: "url(#course-map-green)", markerStart: both ? "url(#course-map-green)" : undefined}

const NodeBox = (props: { n: Node }) => {
    const {n} = props
    const lineHeight = 44
    const top = n.y + n.h / 2 - (n.lines.length - 1) * lineHeight / 2
    return (
        <g>
            <rect x={n.x} y={n.y} width={n.w} height={n.h} rx={RADIUS} fill="#fff"
                  stroke={n.main ? RED : BORDER} strokeWidth={n.main ? 3 : 1.5}/>
            {n.lines.map((line, i) => (
                <text key={i} x={n.x + n.w / 2} y={top + i * lineHeight} textAnchor="middle" dominantBaseline="central"
                      fontSize="31" fill={n.main ? RED : INK} fontFamily={n.main ? FONT_BOLD : FONT}>{line}</text>
            ))}
        </g>)
}

// 圖例裡的小節點
const LegendNode = (props: { x: number, y: number, label: string, main?: boolean }) => (
    <g>
        <rect x={props.x} y={props.y} width={120} height={52} rx={26} fill="#fff"
              stroke={props.main ? RED : BORDER} strokeWidth={props.main ? 3 : 1.5}/>
        <text x={props.x + 60} y={props.y + 27} textAnchor="middle" dominantBaseline="central" fontSize="20"
              fill={props.main ? RED : INK} fontFamily={props.main ? FONT_BOLD : FONT}>{props.label}</text>
    </g>)

export const CourseMap = () => (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="《C++ 從入門到專家》線上系列課程地圖"
         style={{display: "block", fontFamily: FONT, fontSynthesis: "none"}}>
        <defs>
            {/* 中間亮、四周略暗，取樣自原圖 */}
            <radialGradient id="course-map-bg" cx="0.52" cy="0.5" r="0.75">
                <stop offset="0" stopColor="#fdfdfd"/>
                <stop offset="0.6" stopColor="#f3f3f3"/>
                <stop offset="1" stopColor="#e6e6e6"/>
            </radialGradient>
            <marker id="course-map-red" viewBox="0 0 10 10" refX="9" refY="5" markerUnits="userSpaceOnUse"
                    markerWidth="22" markerHeight="22" orient="auto-start-reverse">
                <path d="M0,0 L10,5 L0,10 z" fill={RED}/>
            </marker>
            <marker id="course-map-green" viewBox="0 0 10 10" refX="9" refY="5" markerUnits="userSpaceOnUse"
                    markerWidth="13" markerHeight="13" orient="auto-start-reverse">
                <path d="M0,0 L10,5 L0,10 z" fill={GREEN}/>
            </marker>
        </defs>
        <rect width={W} height={H} rx="6" fill="url(#course-map-bg)"/>
        <text x="46" y="118" fontSize="68" fill={INK} xmlSpace="preserve">
            {"《 C++ 從入門到專家 》"}
            <tspan dx="8" fontSize="47">線上系列課程地圖</tspan>
        </text>
        {EDGES.map((e, i) => <line key={i} {...edgeLine(e)} {...arrowStyle(e.kind, e.both)}/>)}
        {NODES.map((n) => <NodeBox key={n.id} n={n}/>)}
        {DESCRIPTION.map((line, i) => (
            <text key={i} x="95" y={597 + i * 43} fontSize="27" fill={INK} dominantBaseline="central">{line}</text>
        ))}
        <g>
            <rect x="583" y="561" width="626" height="149" fill="#f1f1f1"/>
            <LegendNode x={597} y={579} label="課程 A" main/>
            <line x1="722" y1="605" x2="766" y2="605" stroke={RED} strokeWidth={5} markerEnd="url(#course-map-red)"/>
            <LegendNode x={770} y={579} label="課程 B"/>
            <text x="900" y="605" fontSize="20" fill={RED} fontFamily={FONT_BOLD} dominantBaseline="central">：課程 A 為課程 B 的先修課程</text>
            <LegendNode x={597} y={644} label="課程 A"/>
            <line x1="719" y1="670" x2="766" y2="670" stroke={GREEN} strokeWidth={1.6} markerEnd="url(#course-map-green)"/>
            <LegendNode x={770} y={644} label="課程 B"/>
            <text x="900" y="670" fontSize="20" fill={GREEN} dominantBaseline="central">：修過課程 A 對修習 B 有幫助</text>
        </g>
    </svg>
)
